// dependencies
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Pagination } from "@performyard/py-components";

//components
import DownloadButton from "../../../components.smart/DownloadButton/DownloadButton";
import Button from "../../../components/Button";
import PageHeading from "../../../components/PageHeading";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import EmployeeGoalsTable from "./EmployeeGoalsTable";
import { EmployeeGoalsFilter } from "./Filters";
import Filters from "./Filters";
import ReadableGoalsFilterChips from "./ReadableFilter";

//services
import {
	useCompanyFunction,
	useCurrentUser,
	useRbacEmployeePermissions,
	useSearchService,
	useSearchServicePrefetch,
	useUser,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
//hooks
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { SERVICE_GOAL, getSearch } from "../../../api/services/defs/search-service";
import { Goal } from "../../../api/services/searchServiceResultTypes/goal";

import pubSub from "../../../utils/PubSub";
//utils
import { getFiltersForSearchService, getUserGoalsNavigationLinks } from "./utils";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
//pageTemplate
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../Error";
import { migrateFiltersToNewFormat } from "./Filters/migrateFilters";
//config
import config from "./config";

const EmployeeGoals = () => {
	const { data: me, isLoading } = useCurrentUser();
	const userId = me?.me?.authorization?.payload?.user_id || "";

	const params = useParams();
	const page = params?.page || "mygoals";
	const employeeID = params?.employeeID || userId || "";
	const isNewNav = useIsNewNavigation();
	const headerLabel = page === "teamGoals" ? "My Team's Goals" : "My Goals";
	const { data: employee, isLoading: isUserLoading } = useUser({ userID: employeeID, disabled: !employeeID });
	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(employeeID, "goal", !employeeID);
	const [downloadCsvJobID, setDownloadCSVJobID] = useState<string | null>(null);

	const canUserCreateGoals = rbac?.includes("goals:createGoal");
	const canUserDownloadCSV = rbac?.includes("goals:exportGoalCsv");
	const { value: ffGoals } = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const { companyFunction: goalsEnabled, isLoading: loadingGoalsFF } = useCompanyFunction("goals");

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters: rawFilters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<EmployeeGoalsFilter>({
		defaultSortName: "end_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	/**
	 * Filter Migration:
	 * Employee Goals filter was updated to use new PY-Components, changing the filter format.
	 * Old URLs/bookmarks may contain old format filters (e.g., {status: [{value: "active", label: "Active"}]}).
	 * We migrate these to the new format (e.g., {status: ["active"]}) so old links continue to work.
	 */
	const filters = migrateFiltersToNewFormat(rawFilters || {});

	/**
	 * Save migrated filters back to URL if migration occurred.
	 * This updates the URL with the new format so future visits use the correct format.
	 * Only runs once on mount to avoid infinite loops.
	 */
	useEffect(() => {
		if (rawFilters && Object.keys(rawFilters).length > 0) {
			// Check if migration changed anything by comparing stringified versions
			// We need to normalize both for comparison
			const normalizeForComparison = (f: unknown): unknown => {
				if (!f || typeof f !== "object") return f;
				const normalized = { ...(f as Record<string, unknown>) };
				// Convert arrays with objects to comparable format
				if (Array.isArray(normalized.status)) {
					normalized.status = normalized.status.map((s: unknown) =>
						typeof s === "object" && s !== null && "value" in s ? (s as { value: string }).value : s
					);
				}
				return normalized;
			};
			const rawNormalized = normalizeForComparison(rawFilters);
			const migratedNormalized = normalizeForComparison(filters);
			const needsMigration = JSON.stringify(rawNormalized) !== JSON.stringify(migratedNormalized);
			if (needsMigration) {
				setFilters(filters);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { data, isLoading: isFetchingGoals } = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: getFiltersForSearchService(filters, employeeID, page, employee?.directReports, debouncedSearchValue),
		skip: (pageNumber - 1) * config.pageSize,
		limit: config.pageSize,
		sort: sort,
		defaultErrorMessage: "There was an error getting Goals",
		disabled: isLoading || isUserLoading,
	});

	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getFiltersForSearchService(
				filters,
				employeeID,
				page,
				employee?.directReports,
				debouncedSearchValue
			),
			skip: pageNumber * config.pageSize,
			limit: config.pageSize,
			sort: sort,
			defaultErrorMessage: "There was an error getting Goals",
			disabled: isLoading || isUserLoading,
		},
		3
	);

	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getFiltersForSearchService(
				filters,
				employeeID,
				page,
				employee?.directReports,
				debouncedSearchValue
			),
			skip: (pageNumber - 2) * config.pageSize,
			limit: config.pageSize,
			sort: sort,
			defaultErrorMessage: "There was an error getting Goals",
			disabled: isLoading || isUserLoading,
		},
		pageNumber > 1 ? 1 : 0
	);

	const goals = data?.results || [];
	const totalGoalsCount = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const additionalFiltersCount = Object.keys(filters)?.length;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search Goals..."
		/>
	);

	const drawer = (
		<Filters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = (
		<ReadableGoalsFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
	);

	if (isLoading || isUserLoading || isRbacLoading || loadingGoalsFF) {
		return <ComponentLoader />;
	}

	if ((!ffGoals && !loadingGoalsFF) || (!loadingGoalsFF && goalsEnabled === "disabled")) {
		return <ErrorPage code={403} />;
	}

	const mainContent = (
		<div>
			<EmployeeGoalsTable
				goals={goals}
				isFetchingGoals={isFetchingGoals}
				handleColumnHeaderClick={(sortValue) => setSort(sortValue)}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				key={page}
			/>

			{totalGoalsCount > 0 && (
				<div
					data-testid="paginationContainer"
					className="mx-auto mt-5 flex w-full max-w-[400px] justify-center"
				>
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalGoalsCount / config.pageSize)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const goalWizardBaseUrl = "/goal/wizard/";
	const goalWizardUrlParams = `assignee=${employeeID}`;

	const handleDownloadCsv = async () => {
		const response = await getSearch(
			SERVICE_GOAL,
			getFiltersForSearchService(filters, employeeID, page, employee?.directReports, debouncedSearchValue),
			0,
			0,
			sort,
			true
		);

		const jobID = response?.jobID;
		pubSub.publish("check async frequency", { frequency: 500 });
		setDownloadCSVJobID(jobID);
	};

	const handleClearCsvDownload = () => {
		setDownloadCSVJobID(null);
	};

	const actionButtons = [
		canUserDownloadCSV && (
			<DownloadButton
				autoDownload={true}
				jobID={downloadCsvJobID}
				isHollow={true}
				data-testid="downloadCSVButton"
				onClick={handleDownloadCsv}
				onDownload={handleClearCsvDownload}
			>
				Download CSV
			</DownloadButton>
		),
		canUserCreateGoals && ffGoals && (
			<Button
				className="no-goal-tooltip"
				data-testid="createGoalButton"
				href={`${goalWizardBaseUrl}?${goalWizardUrlParams}`}
				color="green"
				icon={faPlus}
			>
				Create Goal
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeID}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={
				isNewNav ? headerLabel : employee?.name ? `Goals - ${employee?.name} - PerformYard` : "Employee Goals"
			}
			pageHeading={isNewNav ? <PageHeading>{headerLabel}</PageHeading> : undefined}
			navigationLinks={getUserGoalsNavigationLinks(
				userId,
				employeeID,
				employee?.name,
				(employee?.directReports?.length || 0) > 0
			)}
			activeNavigationLinkKey={page}
		/>
	);
};

export default EmployeeGoals;
